import React, { useState, useEffect } from 'react';
// import { base44 } from '@/api/base44Client';
import Icon from '../components/ui/Icon';
import TransactionCard from '../components/transactions/TransactionCard';
import TransactionForm from '../components/transactions/TransactionForm';

// Placeholder data
const placeholderListings = [
  { id: '1', title: '123 Main St', status: 'active' },
  { id: '2', title: '456 Oak Ave', status: 'pending' },
];

const placeholderAgents = [
  { id: '1', full_name: 'Alice Johnson', status: 'active' },
  { id: '2', full_name: 'Bob Smith', status: 'active' },
];

const placeholderTransactions = [
  { id: '1', buyer_name: 'John Doe', listing_id: '1', agent_id: '1', status: 'active', final_price: 500000, commission_amount: 15000 },
  { id: '2', buyer_name: 'Jane Roe', listing_id: '2', agent_id: '2', status: 'closed', final_price: 750000, commission_amount: 22500 },
];

export default function Transactions() {
  const [transactions, setTransactions] = useState([]);
  const [listings, setListings] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

 const loadData = () => {
  setTransactions(placeholderTransactions);
  setListings(placeholderListings);
  setAgents(placeholderAgents);
  setLoading(false);
};

  useEffect(() => { loadData(); }, []);

  const handleSave = async (data) => {
  if (editingTransaction) {
    setTransactions(prev =>
      prev.map(t => (t.id === editingTransaction.id ? { ...t, ...data } : t))
    );
  } else {
    setTransactions(prev => [...prev, { id: Date.now().toString(), ...data }]);
  }
  setShowForm(false);
  setEditingTransaction(null);
  //loadData(); // still calls loadData to refresh the UI
};

  const handleEdit = (transaction) => {
    setEditingTransaction(transaction);
    setShowForm(true);
  };

  const filteredTransactions = transactions.filter(transaction => {
    const listing = listings.find(l => l.id === transaction.listing_id);
    const agent = agents.find(a => a.id === transaction.agent_id);
    const matchesSearch = !search ||
      transaction.buyer_name?.toLowerCase().includes(search.toLowerCase()) ||
      listing?.title?.toLowerCase().includes(search.toLowerCase()) ||
      agent?.full_name?.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'all' ||
      (statusFilter === 'active' && !['closed', 'cancelled'].includes(transaction.status)) ||
      transaction.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const closedTransactions = transactions.filter(t => t.status === 'closed');
  const activeTransactions = transactions.filter(t => !['closed', 'cancelled'].includes(t.status));
  const totalVolume = closedTransactions.reduce((sum, t) => sum + (t.final_price || 0), 0);
  const totalCommission = closedTransactions.reduce((sum, t) => sum + (t.commission_amount || 0), 0);

  return (
    <div style={{ minHeight: '100vh', background: 'var(--slate-50)', padding: '2rem' }}>
      <div style={{ maxWidth: '80rem', margin: '0 auto' }}>
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Transactions</h1>
            <p className="text-slate-500 mt-1">Track deals from offer to closing</p>
          </div>
          <button className="btn btn-primary" onClick={() => { setEditingTransaction(null); setShowForm(true); }}>
            <Icon name="plus" size={16} />
            New Transaction
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-4 gap-4 mb-8">
          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div style={{ padding: '0.5rem', background: 'var(--slate-100)', borderRadius: '0.5rem' }}>
                <Icon name="fileText" size={20} style={{ color: 'var(--slate-600)' }} />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{transactions.length}</p>
                <p className="text-sm text-slate-500">Total</p>
              </div>
            </div>
          </div>
          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div style={{ padding: '0.5rem', background: 'var(--blue-50)', borderRadius: '0.5rem' }}>
                <Icon name="clock" size={20} style={{ color: 'var(--blue-500)' }} />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{activeTransactions.length}</p>
                <p className="text-sm text-slate-500">Active</p>
              </div>
            </div>
          </div>
          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div style={{ padding: '0.5rem', background: 'var(--emerald-50)', borderRadius: '0.5rem' }}>
                <Icon name="trendingUp" size={20} style={{ color: 'var(--emerald-600)' }} />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">${(totalVolume / 1000000).toFixed(1)}M</p>
                <p className="text-sm text-slate-500">Volume</p>
              </div>
            </div>
          </div>
          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div style={{ padding: '0.5rem', background: '#fef3c7', borderRadius: '0.5rem' }}>
                <Icon name="dollarSign" size={20} style={{ color: 'var(--amber-600)' }} />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">${totalCommission.toLocaleString()}</p>
                <p className="text-sm text-slate-500">Commission</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-6">
          <div style={{ position: 'relative', flex: 1, maxWidth: '24rem' }}>
            <Icon name="search" size={16} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--slate-400)' }} />
            <input
              className="input input-with-icon"
              placeholder="Search transactions..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="tabs">
            {['all', 'active', 'closed', 'cancelled'].map(status => (
              <button
                key={status}
                className={`tab ${statusFilter === status ? 'active' : ''}`}
                onClick={() => setStatusFilter(status)}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Transactions */}
        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {[1, 2, 3, 4].map(i => <div key={i} className="skeleton" style={{ height: '10rem' }} />)}
          </div>
        ) : filteredTransactions.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {filteredTransactions.map(transaction => (
              <TransactionCard
                key={transaction.id}
                transaction={transaction}
                listing={listings.find(l => l.id === transaction.listing_id)}
                agent={agents.find(a => a.id === transaction.agent_id)}
                onEdit={handleEdit}
              />
            ))}
          </div>
        ) : (
          <div className="card" style={{ textAlign: 'center', padding: '4rem' }}>
            <Icon name="fileText" size={48} style={{ color: 'var(--slate-300)', margin: '0 auto 1rem' }} />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No transactions found</h3>
            <p className="text-slate-500 mb-4">
              {search ? 'Try adjusting your search' : 'Start by creating your first transaction'}
            </p>
            <button className="btn btn-primary" onClick={() => { setEditingTransaction(null); setShowForm(true); }}>
              <Icon name="plus" size={16} />
              New Transaction
            </button>
          </div>
        )}
      </div>

      <TransactionForm
        open={showForm}
        onClose={() => { setShowForm(false); setEditingTransaction(null); }}
        transaction={editingTransaction}
        listings={listings}
        agents={agents.filter(a => a.status === 'active')}
        onSave={handleSave}
      />
    </div>
  );
}